import styled from '@emotion/styled';
import { AppUI , LoadingState } from '@seedlang/state';
import { isBlank } from '@seedlang/utils';
import autobind from 'autobind-decorator';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InPlaceImageUpload from 'components/form/in_place_image_upload';
import InPlaceLanguageSelect from 'components/form/in_place_language_select';
import InPlaceText from 'components/form/in_place_text';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import Spinner from 'components/spinner';
import Text from 'components/text';
import { observable, autorun, computed } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router';
import PodcastPublicationStateBanner from './podcast_publication_state_banner';
import RssImport from './rss_import';
import { RefreshIcon } from '@seedlang/icons';
import { PodcastMembershipTypeStore } from '@seedlang/stores';
import Alert from 'components/alert';
import { Theme } from '@seedlang/constants';
import Stack from 'components/layout/stack';
import Grid from 'components/layout/grid';
import GrayButton from 'components/button/gray_button';

const Title = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin: 10px 0 5px 0;
`;

const TextWithIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  .icon {
    width: 10px;
    height: 10px;
  }
`;

@observer
class PodcastEdit extends React.Component {
  @observable showSpinner = false;
  @observable coverImageSizeIsIncorrect = false;
  membershipTypesLoadingState = new LoadingState();

  constructor(props) {
    super(props);
    this.getPodcast();
    AppUI.podcastStore.clearIndexData();
    AppUI.membershipTypeStore.getIndex({});
    window.AppUI = AppUI;
    autorun(() => {
      if (AppUI.podcastStore.showData.podcastImage?.image?.url) {
        const image = new Image();
        image.src = AppUI.podcastStore.showData.podcastImage.image.url;
        image.onload = () => {
          this.coverImageSizeIsIncorrect = image.width !== 512 || image.height !== 512;
        };
      } else {
        this.coverImageSizeIsIncorrect = false;
      }
    });
  }

  @autobind async getPodcast() {
    this.showSpinner = true;
    await AppUI.podcastStore.getShowPromise({ids: {podcastId: this.props.params.podcastId}});
    this.showSpinner = false;
  }

  @autobind onUpdateRssFeeds() {
    this.showSpinner = true;
    AppUI.podcastStore.updateRssFeeds({ids: {podcastId: this.props.params.podcastId}}, this.getPodcast);
  }

  @autobind async onUpdateMembershipType(membershipTypeId) {
    this.membershipTypesLoadingState.started();
    try {
      // Check if this membership type is already associated with the podcast
      const existingAssociation = AppUI.podcastStore.showData.podcastMembershipTypes?.find(
        pmt => pmt.membershipType.id === membershipTypeId,
      );

      if (existingAssociation) {
        // Remove the association
        await PodcastMembershipTypeStore.destroyPromise({
          ids: { podcastMembershipTypeId: existingAssociation.id },
        });
      } else {
        // Create a new association
        await PodcastMembershipTypeStore.createPromise({
          data: {
            membership_type_id: membershipTypeId,
            podcast_id: this.props.params.podcastId,
          },
        });
      }

      // Refresh the podcast data to get updated associations
      await AppUI.podcastStore.getShowPromise({ ids: { podcastId: this.props.params.podcastId } });
      this.membershipTypesLoadingState.succeeded();
    }
    catch {
      this.membershipTypesLoadingState.failed('Error updating membership types');
    }
  }

  @computed get coverImageMissing() {
    return !AppUI.podcastStore.showData.podcastImage?.image?.url;
  }

  render() {
    return (
      <Stack gap='sm' alignItems='stretch'>
        <div className='breadcrumbs'>
          <Link
            to='creator.podcasts.index'
          >
            View Podcasts
          </Link>
        </div>
        <Title>Podcast Settings</Title>
        <PodcastPublicationStateBanner
          publicationState={AppUI.podcastStore.showData.publicationState}
          onPublishChanges={this.onUpdateRssFeeds}
          showSpinner={this.showSpinner}
        />
        <Grid columns={2}>
          <fieldset>
            <legend>Podcast Title</legend>
            <InPlaceText
              id={AppUI.podcastStore.showData.id}
              defaultValue={AppUI.podcastStore.showData.title}
              model='podcasts'
              field='title'
              afterChange={this.getPodcast}
            />
          </fieldset>
          <fieldset>
            <legend>Podcast Language</legend>
            <InPlaceLanguageSelect
              model='podcasts'
              id={AppUI.podcastStore.showData.id}
              value={AppUI.podcastStore.showData.language}
              field='language'
              includeBlank
              afterChange={this.getPodcast}
            />
          </fieldset>
        </Grid>

        <Grid columns={4}>
          <fieldset>
            <legend>Podcast Author</legend>
            <InPlaceText
              id={AppUI.podcastStore.showData.id}
              defaultValue={AppUI.podcastStore.showData.author}
              model='podcasts'
              field='author'
              afterChange={this.getPodcast}
            />
          </fieldset>
          <fieldset>
            <legend>Podcast Owner Name</legend>
            <InPlaceText
              id={AppUI.podcastStore.showData.id}
              defaultValue={AppUI.podcastStore.showData.ownerName}
              model='podcasts'
              field='owner_name'
              afterChange={this.getPodcast}
            />
          </fieldset>
          <fieldset>
            <legend>Podcast Owner Email</legend>
            <InPlaceText
              id={AppUI.podcastStore.showData.id}
              defaultValue={AppUI.podcastStore.showData.ownerEmail}
              model='podcasts'
              field='owner_email'
              afterChange={this.getPodcast}
            />
          </fieldset>
          <fieldset>
            <legend>Podcast Copyright Text</legend>
            <InPlaceText
              id={AppUI.podcastStore.showData.id}
              defaultValue={AppUI.podcastStore.showData.copyright}
              model='podcasts'
              field='copyright'
              afterChange={this.getPodcast}
            />
          </fieldset>
        </Grid>
        <Grid columns={2}>
          <div>
            <fieldset>
              <legend>Podcast Cover Image</legend>
              {
                (this.coverImageSizeIsIncorrect || this.coverImageMissing) &&
                  <Alert
                    background={this.coverImageMissing ? AppUI.site.accentColor : Theme.orange}
                  >
                    Please upload a 512x512px image.
                    {this.coverImageSizeIsIncorrect && ' This image will not work with all media players.'}
                  </Alert>
              }
              <InPlaceImageUpload
                image={AppUI.podcastStore.showData.podcastImage && AppUI.podcastStore.showData.podcastImage.image}
                model='podcast_images'
                id={AppUI.podcastStore.showData.podcastImage?.id}
                onChange={this.getPodcast}
                querystring={{
                  podcast_id: AppUI.podcastStore.showData.id,
                }}
              />
            </fieldset>
            <fieldset>
              <legend>Membership</legend>
              <Alert background='gray'>Determines access to the feed as well as the vocab and exercises in podcast posts.</Alert>
              <Stack gap='xs'>
                {
                  AppUI.membershipTypeStore.indexData.map(item => {
                    return (
                      <div className='row' key={item.id}>
                        <InPlaceCheckbox
                          value={AppUI.podcastStore.showData?.podcastMembershipTypes?.some(pmt => pmt.membershipType.id === item.id)}
                          onChange={() => this.onUpdateMembershipType(item.id)}
                          disabled={this.membershipTypesLoadingState.isLoading}
                        >
                          {item.name}
                        </InPlaceCheckbox>
                      </div>
                    );
                  })
                }
              </Stack>
            </fieldset>
          </div>
          <div>
            <fieldset>
              <legend>Website Link</legend>
              <InPlaceText
                id={AppUI.podcastStore.showData.id}
                defaultValue={AppUI.podcastStore.showData.siteLink}
                model='podcasts'
                field='site_link'
                afterChange={this.getPodcast}
              />
            </fieldset>
            <fieldset>
              <legend>SpeakPipe integration</legend>
              <div>
                <Text fontSize='14px'><a href='https://www.speakpipe.com/' className='underline'>SpeakPipe</a> is a service used to receive voice messages from users.</Text>
              </div>
              <InPlaceText
                submitOnEnter
                label='SpeakPipe integration link'
                show
                id={AppUI.podcastStore.showData.id}
                defaultValue={AppUI.podcastStore.showData.voiceMessageLink}
                model='podcasts'
                field='voice_message_link'
                placeholder='https://www.speakpipe.com/widget/inline/repdspsagysgj0jsy2bgqxxc30p56o3g'
                afterChange={this.getPodcast}
              />
              <InPlaceText
                submitOnEnter
                label='Button text'
                show={isBlank(AppUI.podcastStore.showData.voiceMessageButton)}
                id={AppUI.podcastStore.showData.id}
                defaultValue={AppUI.podcastStore.showData.voiceMessageButton}
                model='podcasts'
                field='voice_message_button'
                placeholder='Eure Fragen'
                afterChange={this.getPodcast}
              />
            </fieldset>
          </div>
        </Grid>

        <Grid columns={[1,2,3]}>
          <fieldset>
            <legend title='To reduce the size of the RSS feeds, only the configured amount of episodes will contain the full transcript.'>
              Transcript Limit
            </legend>
            <InPlaceText
              id={AppUI.podcastStore.showData.id}
              defaultValue={AppUI.podcastStore.showData.transcriptLimit}
              model='podcasts'
              field='transcript_limit'
              type='number'
              step='1'
              afterChange={this.getPodcast}
            />
          </fieldset>
          <fieldset>
            <legend title='The header of the transcript. This will be used for removing the transcript from older episodes to limit the size of the feed.'>
              Transcript Title
            </legend>
            <InPlaceText
              id={AppUI.podcastStore.showData.id}
              defaultValue={AppUI.podcastStore.showData.transcriptTitle}
              model='podcasts'
              field='transcript_title'
              afterChange={this.getPodcast}
            />
          </fieldset>
          <fieldset>
            <legend>
              Transcript Link Title
            </legend>
            <InPlaceText
              id={AppUI.podcastStore.showData.id}
              defaultValue={AppUI.podcastStore.showData.transcriptLinkTitle}
              model='podcasts'
              field='transcript_link_title'
              afterChange={this.getPodcast}
            />
          </fieldset>
        </Grid>

        <RssImport
          podcastId={AppUI.podcastStore.showData.id}
          afterImport={this.getPodcast}
        />

        {
          AppUI.user.developer &&
              <fieldset>
                <legend>Developer Tools: RSS Feeds</legend>
                <div>
                  Last Updated: {AppUI.podcastStore.showData.rssUpdatedAt ? AppUI.podcastStore.showData.rssUpdatedAt.formatted : 'never'}
                </div>
                <TextWithIconWrapper>
                  Pending RSS update jobs: {AppUI.podcastStore.showData.rssUpdateNeededCount}
                  <RefreshIcon onClick={this.getPodcast} />
                </TextWithIconWrapper>
                {
                  !this.showSpinner &&
                    <GrayButton
                      style={{marginTop: '10px'}}
                      onClick={this.onUpdateRssFeeds}
                    >
                      Update RSS Feeds
                    </GrayButton>
                }
                {
                  this.showSpinner &&
                    <Spinner />
                }
              </fieldset>
        }
      </Stack>
    );
  }
}

export default ErrorBoundary(PodcastEdit);
